import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { DomUtils } from '@onsolve/onsolve-ui-components';
import keyCodes from 'utils/keyCodes';
import { globalSearchTranslations } from 'translations';

import { clearRecentSearchesHistory, getRecentSearchHistory, removeOneSearchFromHistory } from '@components/GlobalSearch/Services/SearchService/index';
import RecentSearchItems from './RecentSearchItems';
import Ring from '../Utils/ring';

import './RecentSearches.scss';

const { TAB, UP, ENTER_KEY } = keyCodes;

class RecentSearches extends Component {
    constructor(props) {
        super(props);

        this.state = {
            recentSearches: [],
            currentIndex: 0,
            isSelected: false,
            focusCurrentTrash: false
        };

        this.ring = new Ring({ step: 1, currentIndex: 0, elements: [] });
    }

    componentDidMount() {
        this.setRecentSearches();
    }

    componentDidUpdate(prevProps) {
        const { userId } = this.props;

        if (prevProps.userId !== userId) {
            this.setRecentSearches();
        }
    }

    setRecentSearches() {
        const { updateFooter, userId } = this.props;
        const items = getRecentSearchHistory(userId);

        this.ring.elements = items;

        this.setState({ recentSearches: items }, function() {
            updateFooter(items.length > 0);
        });
    }

    handleRemoveAllItems = () => {
        const { userId } = this.props;

        clearRecentSearchesHistory(userId);
        this.setRecentSearches();
    };

    handleRemoveAllByKey = e => {
        const { keyCode, shiftKey } = e;
        const { onTabClick } = this.props;

        if (keyCode === ENTER_KEY) {
            this.handleRemoveAllItems();
        }

        if (keyCode === TAB && !shiftKey) {
            e.preventDefault();
            this.setState({ focusCurrentTrash: false });
            onTabClick();
        }
    };

    removeItem = value => {
        const { userId } = this.props;

        removeOneSearchFromHistory(userId, value);
        this.setRecentSearches();
    };

    unselectItems = () => {
        this.setState({ isSelected: false });
    };

    handleUpKey = e => {
        const { isSelected } = this.state;
        const { addUserTabbing } = this.props;
        const { _ringPower, currentIndex } = this.ring;
        const { forwardedClearRecentRef } = this.props;
        const { keyCode, shiftKey } = e;

        if (keyCode === TAB && shiftKey && currentIndex === 0) {
            DomUtils.setFocus(forwardedClearRecentRef);
            return;
        }

        if (isSelected) {
            this.ring.previous();
            document.body.classList.remove('user-is-tabbing');
            if (keyCode === TAB) {
                addUserTabbing();
                this.setState({ focusCurrentTrash: true });
            }
        } else {
            this.ring.updateCurrent(_ringPower - 1);
        }
        this.setState({ currentIndex: currentIndex, isSelected: true });
    };

    handleDownKey = e => {
        const { isSelected } = this.state;
        const { addUserTabbing } = this.props;
        const { currentIndex } = this.ring;
        const { keyCode } = e;

        if (isSelected) {
            if (keyCode === TAB) {
                addUserTabbing();
                this.setState({ focusCurrentTrash: true });
            } else {
                document.body.classList.remove('user-is-tabbing');
                this.ring.next();
                this.setState({ focusCurrentTrash: false });
            }
        } else {
            this.ring.updateCurrent(0);
        }
        this.setState({ currentIndex: currentIndex, isSelected: true });
    };

    onTrashTab = e => {
        let step;
        const { shiftKey, keyCode } = e;
        const { addUserTabbing, focusOnSearchInput } = this.props;
        const { currentIndex } = this.ring;

        e.preventDefault();
        if (shiftKey || keyCode === UP) {
            step = currentIndex;
        } else {
            // focus on search input if this is the last trash icon
            if (keyCode === TAB && this.ring.isLast) {
                this.setState({ isSelected: false, focusCurrentTrash: false }, focusOnSearchInput);
                return;
            }
            this.ring.next();
            step = currentIndex - 1;
        }

        if (keyCode === TAB) {
            addUserTabbing();
        } else {
            document.body.classList.remove('user-is-tabbing');
        }

        this.setState({ currentIndex: step, isSelected: true, focusCurrentTrash: false });
    };

    selectLastElement = () => {
        const { _ringPower } = this.ring;

        this.ring.updateCurrent(_ringPower - 1);
        this.setState({ currentIndex: this.ring.currentIndex, isSelected: true });
    };

    selectFirstElement = () => {
        const { currentIndex } = this.ring;

        this.ring.updateCurrent(0);
        this.setState({ currentIndex: currentIndex, isSelected: true });
    };

    selectLastElementsTrash = () => {
        const { _ringPower } = this.ring;

        this.ring.updateCurrent(_ringPower - 1);
    };



    render() {
        const { onItemClick, forwardedClearRecentRef, intl } = this.props;
        const { recentSearches, isSelected, focusCurrentTrash } = this.state;

        return recentSearches.length > 0 ? (
            <>
                <div className="onsolve-recent-searches__title-item">
                    <span className="text-secondary">
                        {intl.data[globalSearchTranslations.ng_globalSearch_recentSearches.id]}
                    </span>
                    <span
                        onClick={this.handleRemoveAllItems}
                        onKeyDown={this.handleRemoveAllByKey}
                        className="text-primary pointer"
                        tabIndex={0}
                        ref={forwardedClearRecentRef}
                    >
                        {intl.data[globalSearchTranslations.ng_globalSearch_clearRecent.id]}
                    </span>
                </div>
                <RecentSearchItems
                    onUpKey={this.handleUpKey}
                    onDownKey={this.handleDownKey}
                    onShiftTabKey={this.handleUpKey}
                    onTabKey={this.handleDownKey}
                    recentSearches={recentSearches}
                    isSelected={isSelected}
                    onItemClick={onItemClick}
                    currentIndex={this.ring.currentIndex}
                    removeItem={this.removeItem}
                    focusCurrentTrash={focusCurrentTrash}
                    tabOnTrash={this.onTrashTab}
                />
            </>
        ) : null;
    }
}

RecentSearches.propTypes = {
    addUserTabbing: PropTypes.func,
    focusOnSearchInput: PropTypes.func.isRequired,
    forwardedClearRecentRef: PropTypes.object,
    onItemClick: PropTypes.func,
    onTabClick: PropTypes.func,
    updateFooter: PropTypes.func,
    userId: PropTypes.string,
    intl: PropTypes.object
};

RecentSearches.defaultProps = {
};

export default RecentSearches;
